'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import { useSearchParams } from 'next/navigation';
import { usePathname } from 'i18n/routing';

type PageLoaderContextValue = {
  isLoading: boolean;
  showLoader: (key?: 'static-page' | 'global') => void;
  hideLoader: (key?: 'static-page' | 'global') => void;
  startNavigation: React.TransitionStartFunction;
  navigationInProgressRef: React.RefObject<boolean>;
};

const PageLoaderContext = createContext<PageLoaderContextValue | undefined>(undefined);

export function usePageLoader() {
  const ctx = useContext(PageLoaderContext);
  if (!ctx) throw new Error('usePageLoader must be used within <PageLoaderProvider />');
  return ctx;
}

type PageLoaderProviderProps = React.PropsWithChildren<{
  initialLoading?: boolean;
  overlay?: React.ReactNode;
  minShowMs?: number;
}>;

export const DefaultOverlay = () => (
  <div
    className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/80 backdrop-blur-sm dark:bg-neutral-900/80"
    aria-live="polite"
    aria-busy="true"
  >
    <div className="flex flex-col items-center gap-3">
      <div
        data-ffid="img-page-loader"
        className="size-30 animate-spin rounded-full border-2 border-[#018463] border-t-transparent"
        aria-hidden="true"
        data-testid="spinner"
      />
    </div>
  </div>
);

// Freezes the viewport at `y` so nothing (layout shifts, focus changes, etc.)
// can move the visible scroll position while a manual loader overlay is up.
const lockScroll = (y: number) => {
  if (typeof document === 'undefined') return;

  const { body } = document;

  body.style.position = 'fixed';
  body.style.top = `-${y}px`;
  body.style.left = '0';
  body.style.right = '0';
  body.style.width = '100%';
};

// Reverses `lockScroll` and snaps the viewport back to `y` in the same tick,
// so release is visually indistinguishable from staying put the whole time.
const unlockScroll = (y: number) => {
  if (typeof document === 'undefined') return;

  const { body } = document;

  body.style.position = '';
  body.style.top = '';
  body.style.left = '';
  body.style.right = '';
  body.style.width = '';
  window.scrollTo(0, y);
};

export const PageLoaderProvider: React.FC<PageLoaderProviderProps> = ({
  children,
  initialLoading = false,
  overlay,
  minShowMs = 1000,
}) => {
  const globalActiveRef = useRef(false);
  const staticCountRef = useRef(0);
  const isInitialLoadRef = useRef(true);
  const [isLoading, setIsLoading] = useState<boolean>(initialLoading);
  // Keep navigation loading independent of menu unmounts and manual loaders.
  const [isNavigationPending, startNavigation] = useTransition();
  const isPageLoading = isLoading || isNavigationPending;
  const navigationInProgressRef = useRef(false);
  // Scroll position captured when a manual (non-route-change) loader activates;
  // the viewport is frozen here (see lockScroll) and snapped back on hide, so
  // toggling the overlay never visibly moves the page. Route changes
  // intentionally skip this (scrolling to top on navigation is expected).
  const scrollLockYRef = useRef<number | null>(null);
  const isRouteChangeRef = useRef(false);
  const updateVisibility = useCallback(() => {
    const isStaticActive = staticCountRef.current > 0;
    const isGlobalActive = globalActiveRef.current;
    setIsLoading(isGlobalActive || isStaticActive);
  }, []);

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const routeKey = `${pathname}?${searchParams.toString()}`;

  const showLoader = useCallback(
    (key: 'global' | 'static-page' = 'global') => {
      const wasActive = globalActiveRef.current || staticCountRef.current > 0;

      if (key === 'global') {
        globalActiveRef.current = true;
      } else {
        staticCountRef.current += 1;
      }

      if (!wasActive && !isRouteChangeRef.current && typeof window !== 'undefined') {
        const y = window.scrollY;
        scrollLockYRef.current = y;
        lockScroll(y);
      }

      updateVisibility();
    },
    [updateVisibility],
  );

  const hideLoader = useCallback(
    (key: 'global' | 'static-page' = 'global') => {
      if (key === 'global') {
        globalActiveRef.current = false;
      } else {
        staticCountRef.current -= 1;
        if (staticCountRef.current < 0) staticCountRef.current = 0;
      }

      updateVisibility();
    },
    [updateVisibility],
  );

  // Release the scroll lock once the overlay for a manual loader fully hides,
  // snapping back to the frozen position so toggling it doesn't jump the page.
  useEffect(() => {
    if (isPageLoading) return;
    if (scrollLockYRef.current === null) return;

    const targetY = scrollLockYRef.current;
    scrollLockYRef.current = null;
    unlockScroll(targetY);
  }, [isPageLoading]);

  // Safety net: never leave the page scroll-locked if the provider unmounts
  // while a manual loader is still active.
  useEffect(() => {
    return () => {
      if (scrollLockYRef.current !== null) {
        unlockScroll(scrollLockYRef.current);
        scrollLockYRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }
    isRouteChangeRef.current = true;
    if (scrollLockYRef.current !== null) {
      unlockScroll(scrollLockYRef.current);
      scrollLockYRef.current = null;
    }
    showLoader();
    document.documentElement.classList.add('overflow-hidden');
    const t = globalThis.window.setTimeout(() => {
      hideLoader();
      document.documentElement.classList.remove('overflow-hidden');
      isRouteChangeRef.current = false;
    }, minShowMs);

    return () => {
      globalThis.window.clearTimeout(t);
      document.documentElement.classList.remove('overflow-hidden');
      isRouteChangeRef.current = false;
    };
  }, [pathname]);

  useEffect(() => {
    if (!navigationInProgressRef.current) return;

    navigationInProgressRef.current = false;
    hideLoader();
  }, [routeKey]);

  const value = useMemo(
    () => ({ isLoading: isPageLoading, showLoader, hideLoader, startNavigation, navigationInProgressRef }),
    [isPageLoading, showLoader, hideLoader, startNavigation, navigationInProgressRef],
  );

  return (
    <PageLoaderContext.Provider value={value}>
      {children}

      <div
        style={{
          pointerEvents: isPageLoading ? 'auto' : 'none',
          opacity: isPageLoading ? 1 : 0,
          zIndex: isPageLoading ? 999999 : 'auto',
        }}
        className="absolute inset-0 transition-opacity duration-150"
      >
        {isPageLoading ? (overlay ?? <DefaultOverlay />) : null}
      </div>
    </PageLoaderContext.Provider>
  );
};
